import type { ParamSpec } from "./types";

/**
 * Deciding how a declared action parameter is edited, and normalising what gets sent back.
 *
 * Free of React so the mapping is unit-testable: the widget a param gets is derived from its
 * declared type, and getting that wrong means either an unsaveable value or a free-text box where
 * a scoped picker was the whole point.
 */

export type ParamWidget = "checkbox" | "picker" | "text";

/**
 * The widget for a param.
 *
 * Reference params (``user``, ``form_definition``) get a picker restricted to the choices the
 * server resolved for this workflow's owner — a free-text id would let a config point at a user
 * of another company, which the API rejects and the engine would otherwise silently skip.
 */
export function widgetFor(param: ParamSpec): ParamWidget {
    if (param.type === "boolean") {
        return "checkbox";
    }
    if (param.isReference) {
        return "picker";
    }
    return "text";
}

/** The value to show in the control for a stored param value. */
export function displayValue(
    param: ParamSpec,
    stored: unknown,
): string | boolean {
    if (widgetFor(param) === "checkbox") {
        return stored === true || stored === "true";
    }
    return stored === undefined || stored === null ? "" : String(stored);
}

/**
 * Normalise a draft bundle for submission.
 *
 * Empty values are dropped rather than sent: storing an empty string would shadow the action's
 * own fallback, which is a different behaviour from leaving the param unset. The server drops
 * them too — doing it here as well keeps the draft comparison honest, so a field the user
 * cleared does not read as a pending change forever.
 */
export function normaliseParams(
    specs: ParamSpec[],
    draft: Record<string, unknown>,
): Record<string, unknown> {
    const declared = new Set(specs.map((spec) => spec.name));
    const normalised: Record<string, unknown> = {};

    for (const [name, value] of Object.entries(draft)) {
        if (!declared.has(name)) {
            // Not offered by the action any more. Preserved on the server for old configs, so it
            // is not the client's business to resend or to drop it.
            continue;
        }
        if (value === "" || value === null || value === undefined) {
            continue;
        }
        if (value === false) {
            // An unchecked box means "not set", matching the admin widget, where an unchecked
            // checkbox posts nothing at all.
            continue;
        }
        normalised[name] = value;
    }

    return normalised;
}

/** Whether a draft differs from what is stored, once both are normalised the same way. */
export function paramsChanged(
    specs: ParamSpec[],
    stored: Record<string, unknown>,
    draft: Record<string, unknown>,
): boolean {
    const left = normaliseParams(specs, stored);
    const right = normaliseParams(specs, draft);

    const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
    for (const key of keys) {
        if (String(left[key] ?? "") !== String(right[key] ?? "")) {
            return true;
        }
    }
    return false;
}

/**
 * Whether a stored reference value points at something outside the resolved choices.
 *
 * Happens after a copy to another company, or when a user leaves. The picker has to say so
 * rather than silently showing an empty select, because the stored value is still what the
 * engine will try to use.
 */
export function isDanglingReference(
    param: ParamSpec,
    stored: unknown,
): boolean {
    if (
        !param.isReference ||
        stored === undefined ||
        stored === null ||
        stored === ""
    ) {
        return false;
    }
    return !param.choices.some((choice) => choice.value === String(stored));
}
